import { useEffect, useRef, useState } from 'react';

/**
 * Hook for scroll-triggered reveal animations
 * @param {Object} options
 * @param {number} options.threshold - Percentage of element visible to trigger (0-1)
 * @param {boolean} options.once - Only trigger once (default: true)
 * @returns {[React.RefObject, boolean]} - Ref to attach to element, and revealed state
 */
export function useScrollReveal({ threshold = 0.15, once = true } = {}) {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsRevealed(false);
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, once]);

  return [ref, isRevealed];
}

/**
 * Hook for staggered scroll reveal on multiple children
 * @param {number} itemCount - Number of items to animate
 * @param {Object} options
 * @param {number} options.threshold - Percentage of container visible to trigger
 * @param {number} options.staggerDelay - Delay between each item in ms
 * @returns {[React.RefObject, boolean[]]} - Ref for container, array of revealed states
 */
export function useStaggeredReveal(itemCount, { threshold = 0.15, staggerDelay = 50 } = {}) {
  const ref = useRef(null);
  const [revealedItems, setRevealedItems] = useState(Array(itemCount).fill(false));
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasTriggered.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;

          // Stagger the reveals
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setRevealedItems(prev => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * staggerDelay);
          }

          observer.unobserve(element);
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [itemCount, threshold, staggerDelay]);

  return [ref, revealedItems];
}

export default useScrollReveal;
